import { useEffect, useState } from "react";
import api, { apiError, brl, rows } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy, ExternalLink, Package, Eye, EyeOff, Tag, Merge } from "lucide-react";
import ProductForm from "./products/ProductForm";
import LabelDialog from "./products/LabelDialog";
import MergeDialog from "./products/MergeDialog";
import { ConfirmDialog } from "@/components/panel/ui";
import DeleteProductDialog from "./products/DeleteProductDialog";
import Pagination from "@/components/panel/Pagination";
import Autocomplete from "@/components/panel/Autocomplete";
import usePaginatedList from "@/hooks/usePaginatedList";
import { Badge, Button, DataTable, FilterSheet, PageHeader, SearchInput, Select, Thumb, Tooltip } from "@/components/ui-kit";

export default function AdminProducts() {
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [stock, setStock] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [brand, setBrand] = useState("");
  const [featured, setFeatured] = useState("");
  const [promo, setPromo] = useState("");
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deactivating, setDeactivating] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [labeling, setLabeling] = useState(null);
  const [merging, setMerging] = useState(null);

  // Busca e filtros vão ao servidor: antes só encontravam algo dentro dos 500
  // primeiros produtos carregados.
  const {
    items: products, total, page, lastPage, setPage, search, setSearch, refetch,
    sort, setSort, isLoading, isError,
  } = usePaginatedList("/admin/products", {
    category: category || undefined,
    status: status || undefined,
    stock: stock || undefined,
    supplier_id: supplierId || undefined,
    brand: brand || undefined,
    featured: featured || undefined,
    promo: promo || undefined,
  });
  const load = refetch;
  const hasFilters = !!(search || category || status || stock || supplierId || brand || featured || promo);

  // Marcas já usadas: alimentam o autocompletar do cadastro e este filtro.
  // Lista curta e estável (dezenas, não milhares), então vem inteira de uma vez —
  // diferente de fornecedor, que precisa de busca paginada.
  const [brands, setBrands] = useState([]);

  // Fornecedor usa o mesmo padrão de "picker" do formulário (busca + select):
  // a base tem mais de 10 mil fornecedores, então um select de página única
  // mostraria só o começo do alfabeto. Sem `situacao` de propósito (= todos):
  // produto antigo pode estar ligado a fornecedor já desativado e ainda assim
  // precisa ser filtrável.
  const supplierPickerFilter = usePaginatedList("/admin/suppliers", {}, { perPage: 100 });
  // Nome do selecionado guardado à parte: ao mudar a busca ele sai de `items` e
  // o select ficaria em branco com o filtro ainda ativo.
  const [supplierName, setSupplierName] = useState("");

  const clearFilters = () => {
    setSearch(""); setCategory(""); setStatus(""); setStock("");
    setSupplierId(""); setSupplierName(""); setBrand(""); setFeatured(""); setPromo("");
    supplierPickerFilter.setSearch("");
  };

  const loadBrands = () => api.get("/admin/products/brands").then((r) => setBrands(r.data || [])).catch(() => {});

  useEffect(() => {
    // Endpoint do ADMIN: o público esconde as categorias fora da loja virtual,
    // e o cadastro precisa oferecer todas — inclusive as de uso interno, que só
    // existem para o balcão.
    api.get("/admin/categories").then((r) => setCategories(rows(r))).catch(() => {});
    loadBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tirar da loja e apagar do cadastro são coisas diferentes: a primeira preserva
  // o histórico de vendas, a segunda só é possível em produto que nunca foi usado.
  const setActive = async (p, active) => {
    try {
      await api.patch(`/admin/products/${p.id}`, { active });
      toast.success(`${p.name} ${active ? "reativado" : "desativado"}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const duplicate = (p) => {
    setEditing({
      ...p, id: undefined, name: `${p.name} (cópia)`, active: false,
      variants: p.variants.map((v) => ({ label: v.label, stock: 0, price_delta: v.price_delta })),
    });
    setShowForm(true);
  };

  // Mesmos controles para a linha (desktop) e para a folha (mobile):
  // declarados uma vez para não divergirem.
  const filtros = (
    <>
            <Autocomplete
              value={category} onChange={(v) => setCategory(v)}
              options={categories.map((c) => ({ value: c.slug, label: c.name }))}
              ariaLabel="Filtrar por categoria"
              placeholder="Todas categorias" testId="admin-products-category-filter" width="w-48" />
            <Select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-products-status-filter"
              aria-label="Filtrar por status" className="w-auto px-2.5 text-slate-600">
              <option value="">Todos status</option>
              <option value="true">Ativos</option>
              <option value="false">Inativos</option>
            </Select>
            <Select value={stock} onChange={(e) => setStock(e.target.value)} data-testid="admin-products-stock-filter"
              aria-label="Filtrar por estoque" className="w-auto px-2.5 text-slate-600">
              <option value="">Todo estoque</option>
              <option value="sem">Sem estoque</option>
              <option value="baixo">Estoque baixo</option>
              <option value="com">Com estoque</option>
            </Select>
            {/* Marca só aparece quando existe alguma cadastrada: numa base que
                ainda não usa o campo, um filtro vazio é só mais um controle
                ocupando a barra. */}
            {brands.length > 0 && (
              <Autocomplete
                value={brand} onChange={(v) => setBrand(v)}
                options={brands.map((b) => ({ value: b, label: b }))}
                ariaLabel="Filtrar por marca"
                placeholder="Todas marcas" testId="admin-products-brand-filter" width="w-44" />
            )}
            {/* Busca no servidor: são ~11 mil fornecedores, uma lista fixa não serve. */}
            <Autocomplete
              value={supplierId}
              onChange={(v, l) => { setSupplierId(v); setSupplierName(l); }}
              options={supplierPickerFilter.items.map((s) => ({ value: s.id, label: s.name }))}
              query={supplierPickerFilter.search} onQueryChange={supplierPickerFilter.setSearch}
              selectedLabel={supplierName}
              ariaLabel="Filtrar por fornecedor"
              placeholder="Todos fornecedores" testId="admin-products-supplier-filter" width="w-56" />
            <Select value={featured} onChange={(e) => setFeatured(e.target.value)} data-testid="admin-products-featured-filter"
              aria-label="Filtrar por destaque" className="w-auto px-2.5 text-slate-600">
              <option value="">Todos destaques</option>
              <option value="true">Em destaque</option>
              <option value="false">Sem destaque</option>
            </Select>
            <Select value={promo} onChange={(e) => setPromo(e.target.value)} data-testid="admin-products-promo-filter"
              aria-label="Filtrar por promoção" className="w-auto px-2.5 text-slate-600">
              <option value="">Promoção: todas</option>
              <option value="1">Só em promoção</option>
            </Select>
            {hasFilters && (
              <Button variant="link" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">Limpar filtros</Button>
            )}
    </>
  );
  const filtrosAtivos = [category, status, stock, supplierId, brand, featured, promo].filter(Boolean).length;

  return (
    <div className="space-y-5" data-testid="admin-products-page">
      <PageHeader
        eyebrow="Operação"
        title="Produtos"
        icon={Package}
        description="Catálogo da loja e do balcão. Desativar tira da loja e preserva o histórico; excluir só vale para produto que nunca foi vendido. Cadastro duplicado vincula-se ao certo, levando o histórico junto."
        actions={(
          <>
            <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nome, código de barras, NCM..." data-testid="admin-products-search" aria-label="Buscar produto por nome, código de barras ou NCM" width="w-64" />
            <Button icon={Plus} onClick={() => { setEditing(null); setShowForm(true); }} data-testid="admin-new-product-btn">
              Novo produto
            </Button>
          </>
        )}
        filters={filtros}
        mobileFilters={<FilterSheet count={filtrosAtivos} onClear={clearFilters}>{filtros}</FilterSheet>}
      />
      <DataTable
        caption="Produtos do catálogo"
        rows={products}
        sort={sort}
        onSortChange={setSort}
        rowKey={(p) => p.id}
        loading={isLoading}
        error={isError}
        onRetry={load}
        empty={{
          icon: Package,
          title: hasFilters ? "Nenhum produto com esses filtros" : "Nenhum produto cadastrado",
          desc: hasFilters
            ? "Ajuste a busca ou limpe os filtros aplicados."
            : "Cadastre o primeiro produto para ele aparecer na loja.",
          action: hasFilters
            ? <Button variant="secondary" onClick={clearFilters}>Limpar filtros</Button>
            : <Button icon={Plus} onClick={() => { setEditing(null); setShowForm(true); }}>Cadastrar primeiro produto</Button>,
          testId: "products-empty",
        }}
        columns={[
          {
            key: "produto", header: "Produto", sortable: true,
            cell: (p) => (
              // O testid da linha ficava no <tr>, que agora é do DataTable
              // externa: continua uma ocorrência por produto.
              <div className="flex items-center gap-3" data-testid="admin-product-row">
                <Thumb src={p.image} iconSize={14} className="w-9 h-9 rounded-md object-cover border border-slate-100 shrink-0" />
                <div className="min-w-0">
                  <span className="block text-slate-900 font-medium">{p.name}</span>
                </div>
              </div>
            ),
          },
          {
            /* Marca em coluna própria, e não mais como subtítulo do nome.
               Escondida sob o nome ela não dava para percorrer com o olho: a
               pergunta do balcão é "quais são os da Mercur?", e responder isso
               exige a marca alinhada na mesma coluna em todas as linhas. No
               celular o DataTable vira lista de definições, então a marca sai
               rotulada em vez de sumir. */
            key: "marca", header: "Marca", width: "9rem", sortable: true,
            className: "text-slate-600",
            cell: (p) => p.brand || <span className="text-slate-300">—</span>,
          },
          { key: "categoria", header: "Categoria", sortable: true, className: "text-slate-500", cell: (p) => p.category_slug || "—" },
          { key: "preco", header: "Preço", align: "right", mono: true, sortable: true, cell: (p) => brl(p.price) },
          {
            key: "estoque", header: "Estoque total", align: "center", mono: true, sortable: true,
            /* Sem escala vertebral aqui, de propósito: esta coluna soma as
               variações, então os valores ficam muito acima do limiar de 5
               e a escala aparecia cheia em quase toda linha — ruído, não
               sinal. A escala fica onde a pergunta é "está baixo?"
               (Estoque, Consulta e Estoque baixo do Dashboard). */
            cell: (p) => {
              const total = p.variants.reduce((s, v) => s + v.stock, 0);
              return <span className={total <= 5 ? "text-amber-600 font-semibold" : "text-slate-700"}>{total}</span>;
            },
          },
          {
            key: "status", header: "Status", align: "center", sortable: true,
            // Ativo é o estado que rende dinheiro (emerald); inativo é ausência
            // de estado, não erro — por isso neutro, e não vermelho.
            cell: (p) => <Badge tone={p.active ? "success" : "neutral"}>{p.active ? "Ativo" : "Inativo"}</Badge>,
          },
          {
            key: "acoes", header: "Ações", align: "center", className: "whitespace-nowrap",
            cell: (p) => (
              <>
                {/* Link de verdade (abre a loja em outra aba), então <a> e não
                    Button — só ganha o mesmo desenho dos botões de ícone. O balão
                    vem por fora porque quem o embute é o Button, e aqui não há um:
                    sem isto, o primeiro ícone da fileira seria o único mudo. */}
                <Tooltip label="Ver na loja">
                  <a href={`/produto/${p.slug}`} target="_blank" rel="noreferrer" data-testid="admin-view-product-btn"
                    aria-label={`Ver ${p.name} na loja`}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-brand-600 transition-colors"><ExternalLink size={15} /></a>
                </Tooltip>
                <Button variant="ghost" size="iconSm" onClick={() => { setEditing(p); setShowForm(true); }}
                  data-testid="admin-edit-product-btn" title="Editar" aria-label={`Editar ${p.name}`}
                  className="hover:text-brand-600"><Pencil size={15} /></Button>
                <Button variant="ghost" size="iconSm" onClick={() => duplicate(p)}
                  data-testid="admin-duplicate-product-btn" title="Duplicar" aria-label={`Duplicar ${p.name}`}
                  className="hover:text-brand-600"><Copy size={15} /></Button>
                {/* Etiqueta fica na linha do produto, e não dentro do cadastro:
                    quem imprime está repondo prateleira, não editando produto. */}
                <Button variant="ghost" size="iconSm" onClick={() => setLabeling(p)}
                  data-testid="admin-label-product-btn" title="Imprimir etiqueta" aria-label={`Imprimir etiqueta de ${p.name}`}
                  className="hover:text-brand-600"><Tag size={15} /></Button>
                {p.active
                  ? <Button variant="ghost" size="iconSm" onClick={() => setDeactivating(p)}
                      data-testid="admin-deactivate-product-btn" title="Desativar (sai da loja, histórico mantido)"
                      aria-label={`Desativar ${p.name}`} className="hover:text-amber-600"><EyeOff size={15} /></Button>
                  : <Button variant="ghost" size="iconSm" onClick={() => setActive(p, true)}
                      data-testid="admin-reactivate-product-btn" title="Reativar" aria-label={`Reativar ${p.name}`}
                      className="hover:text-emerald-600"><Eye size={15} /></Button>}
                {/* Fica ao lado do excluir porque é a saída de quem esbarrou
                    nele: produto com histórico não pode ser apagado, e o
                    duplicado da migração é exatamente esse caso. */}
                <Button variant="ghost" size="iconSm" onClick={() => setMerging(p)}
                  data-testid="admin-merge-product-btn" title="Vincular a outro produto (cadastro duplicado)"
                  aria-label={`Vincular ${p.name} a outro produto`}
                  className="hover:text-brand-600"><Merge size={15} /></Button>
                <Button variant="ghost" size="iconSm" onClick={() => setRemoving(p)}
                  data-testid="admin-remove-product-btn" title="Excluir do cadastro" aria-label={`Excluir ${p.name}`}
                  className="hover:text-red-500"><Trash2 size={15} /></Button>
              </>
            ),
          },
        ]}
      />
      <Pagination page={page} lastPage={lastPage} total={total} onChange={setPage} label="produtos" />
      {/* `loadBrands` no salvar: uma marca nova precisa aparecer no autocompletar
          da próxima vez sem exigir F5. */}
      {showForm && <ProductForm product={editing} categories={categories} brands={brands}
        onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); loadBrands(); }} />}
      {labeling && <LabelDialog product={labeling} onClose={() => setLabeling(null)} />}
      {merging && <MergeDialog product={merging} onClose={() => setMerging(null)}
        onDone={() => { setMerging(null); load(); }} />}
      <ConfirmDialog
        open={!!deactivating}
        onClose={() => setDeactivating(null)}
        onConfirm={() => { setActive(deactivating, false); setDeactivating(null); }}
        title="Desativar produto"
        message={deactivating ? `Desativar "${deactivating.name}"? Ele sai da loja imediatamente, mas o histórico de vendas é mantido e você pode reativá-lo depois.` : ""}
        confirmLabel="Desativar"
      />
      {/* Diálogo próprio, e não o ConfirmDialog genérico: ele consulta o
          histórico do produto antes de mostrar qualquer botão e muda o que
          oferece conforme a resposta — com venda, pede justificativa. */}
      {removing && (
        <DeleteProductDialog
          product={removing}
          onClose={() => setRemoving(null)}
          onDeleted={() => { setRemoving(null); load(); }}
        />
      )}
    </div>
  );
}
